import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ChatIcon from '@mui/icons-material/Chat';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { fetchClients } from '@src/api/actions/clients';
import clientInboxApi from '@src/api/clientInbox';
import { useMessage } from '@src/context/common';

const ALL_CLIENTS = '__all__';

// Quick-action presets derived from a 90-day inbox-intent mining pass:
// the volumes that drove this list are documented in client_inbox_gap_analysis.
// `prompt` is what gets sent to the agent — phrased to kick off the right
// tool flow. `label` is what the client sees on the chip.
const QUICK_QUESTIONS = [
  {
    label: "How's my performance?",
    prompt: "Give me a summary of my campaign performance this month — spend, leads, CPL, and how I'm pacing against budget.",
  },
  {
    label: "How's my budget pacing?",
    prompt: "How is my budget pacing this month? Show me total spent, remaining, and which segments are over- or under-pacing.",
  },
  {
    label: 'Compare to last month',
    prompt: 'Compare my performance this month vs last month — spend, leads, CPL, and booked appointments. Highlight the biggest changes.',
  },
  {
    label: 'Show me my leads',
    prompt: "Show me a summary of my leads this month — total volume, quality breakdown, top zips, and how many self-booked.",
  },
];

// Creative is intentionally NOT in this list. Creative is 100% designed in-house
// by Lead Gurus within the bounds of each client's offers/warranties — clients
// do not concept, request, or review creative through the agent.
const QUICK_REQUESTS = [
  {
    label: 'Change my budget',
    prompt: "I'd like to change my budget. Ask me what you need (amount, segment, when to take effect) so you can submit the request.",
  },
  {
    label: 'Update my ZIP coverage',
    prompt: "I'd like to add or remove ZIP codes from my coverage. Show me current coverage first, then help me put in the request.",
  },
  {
    label: 'Pause / restart campaigns',
    prompt: "I need to pause or restart a campaign. Ask me what you need (which scope, when to take effect, why) and submit the request.",
  },
];

function AgentChatPage() {
  const message = useMessage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [turns, setTurns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Session state
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // In All Clients mode, track which client a new chat is for
  const [newChatClientId, setNewChatClientId] = useState('');

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Attachments pending on the next send. Each entry:
  //   {name, content_type, data_base64 (images) | data_text (text/csv), size}
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const ATTACHMENT_LIMITS = {
    maxCount: 3,
    maxImageBytes: 5 * 1024 * 1024,
    maxTextBytes: 50 * 1024,
    imageTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
    textTypes: ['text/csv', 'text/plain', 'application/csv', 'application/json'],
  };

  // --- Voice input (browser-native SpeechRecognition, same pattern as ClientInboxPage) ---
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const voiceSupportedRef = useRef(
    typeof window !== 'undefined'
      && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition),
  );

  const isAllClients = selectedClientId === ALL_CLIENTS;

  const clientsById = {};
  clients.forEach((c) => { clientsById[c.id] = c; });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Consume deep-link params from other pages (ClientSchedulePage's
  // "ask the agent" buttons, the global AgentChatDock's "Open in full
  // page" button). Supports:
  //   ?prompt=<text>     — message to send
  //   ?autosend=1        — fire handleSend automatically once client is ready
  //   ?client=<id|slug>  — pre-select this client before sending
  //   ?session=<id>      — pre-select this session (dock continuity)
  // We strip all four on mount so back/forward doesn't replay.
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingDeepLinkRef = useRef(null);
  // Tab title — restored to whatever the layout had on unmount so users
  // jumping between tabs see "Guru" while here.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Guru — Lead Gurus';
    return () => {
      document.title = prev;
    };
  }, []);

  // Auto-focus the input when the page mounts — clients can start typing
  // immediately without an extra click. Small timeout so the input has
  // rendered by the time we reach for it.
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    const prompt = searchParams.get('prompt');
    const autosend = searchParams.get('autosend');
    const clientParam = searchParams.get('client');
    const sessionParam = searchParams.get('session');
    if (!prompt && !clientParam && !sessionParam) return;
    pendingDeepLinkRef.current = {
      prompt: prompt || null,
      autosend: autosend === '1' || autosend === 'true',
      clientParam: clientParam || null,
      sessionParam: sessionParam || null,
      sent: false,
      sessionApplied: false,
    };
    // Pre-fill the input as a fallback for the no-autosend path.
    if (prompt) setInputValue(prompt);
    const next = new URLSearchParams(searchParams);
    next.delete('prompt');
    next.delete('autosend');
    next.delete('client');
    next.delete('session');
    setSearchParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  // Load clients
  useEffect(() => {
    fetchClients()
      .then((response) => {
        const data = response.data?.results || response.data || [];
        const normalized = (Array.isArray(data) ? data : []).map((client) => ({
          id: String(client.id),
          name: client.name || 'Unnamed client',
          slug: client.slug || '',
        }));
        normalized.sort((a, b) => a.name.localeCompare(b.name));
        setClients(normalized);
        // Auto-pick when the user only has one client (the typical
        // client-member case). Staff users see the dropdown as before.
        if (normalized.length === 1) {
          setSelectedClientId(normalized[0].id);
        }
      })
      .catch(() => {
        message.error('Unable to load clients.');
      });
  }, [message]);

  // Load sessions when client selection changes
  const loadSessions = useCallback(() => {
    if (!selectedClientId) {
      setSessions([]);
      setActiveSessionId(null);
      setTurns([]);
      return;
    }
    setSessionsLoading(true);
    const params = { ordering: '-updated_at' };
    if (!isAllClients) {
      params.client = selectedClientId;
    }
    clientInboxApi
      .listChatSessions(params)
      .then((response) => {
        const data = response.data?.results || response.data || [];
        setSessions(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        message.error('Unable to load sessions.');
      })
      .finally(() => {
        setSessionsLoading(false);
      });
  }, [isAllClients, message, selectedClientId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Load history when session changes
  const loadHistory = useCallback(() => {
    if (!activeSessionId) {
      setTurns([]);
      return;
    }
    setLoading(true);
    clientInboxApi
      .getChatHistory({ session: activeSessionId, limit: 50 })
      .then((response) => {
        setTurns(response.data?.turns || []);
        setTimeout(scrollToBottom, 100);
      })
      .catch(() => {
        message.error('Unable to load chat history.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [activeSessionId, message, scrollToBottom]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Start new chat
  const handleNewChat = useCallback(() => {
    setActiveSessionId(null);
    setTurns([]);
    setInputValue('');
    setNewChatClientId('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleSelectSession = useCallback((sessionId) => {
    setActiveSessionId(sessionId);
  }, []);

  // Determine the effective client for sending
  const activeSession = sessions.find((s) => s.id === activeSessionId);
  const effectiveClientId = isAllClients
    ? (activeSession ? String(activeSession.client) : newChatClientId)
    : selectedClientId;
  const effectiveClientName = clientsById[effectiveClientId]?.name || '';

  // Send message. Accepts an optional `override` so quick-action chips can
  // send their preset prompt without round-tripping through input state.
  const handleSend = useCallback((override) => {
    const raw = typeof override === 'string' ? override : inputValue;
    const trimmed = (raw || '').trim();
    // Allow sending if there's text OR at least one attachment — the model
    // can answer "what's in this screenshot?" with no accompanying prose.
    if ((!trimmed && !pendingAttachments.length) || !effectiveClientId || sending) return;

    const displayContent = trimmed
      || (pendingAttachments.length === 1
        ? `(attached ${pendingAttachments[0].name})`
        : `(attached ${pendingAttachments.length} files)`);
    const tempUserTurn = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: displayContent,
      created_at: new Date().toISOString(),
    };
    setTurns((prev) => [...prev, tempUserTurn]);
    setInputValue('');
    setSending(true);
    setTimeout(scrollToBottom, 50);

    // Insert a placeholder assistant turn that text_delta events will fill in
    // as they stream in from the server. Same id is held in a ref so we can
    // mutate it in-place across multiple events.
    const streamingId = `assistant-${Date.now()}`;
    setTurns((prev) => {
      const cleared = prev.map((t) => (
        t.pending_confirmation ? { ...t, pending_confirmation: false } : t
      ));
      return [...cleared, {
        id: streamingId,
        role: 'assistant',
        content: '',
        tool_calls: [],
        pending_confirmation: false,
        streaming: true,
        active_tool: null,
        created_at: new Date().toISOString(),
      }];
    });

    let gotAnyEvent = false;
    let finalized = false;

    const attachmentsToSend = pendingAttachments.map((a) => {
      const out = { name: a.name, content_type: a.content_type };
      if (a.data_base64) out.data_base64 = a.data_base64;
      if (a.data_text) out.data_text = a.data_text;
      return out;
    });
    // Clear the visible chips immediately — the request has them now.
    if (pendingAttachments.length) setPendingAttachments([]);

    clientInboxApi.streamChatMessage(
      {
        client: effectiveClientId,
        message: trimmed,
        session: activeSessionId || undefined,
        attachments: attachmentsToSend.length ? attachmentsToSend : undefined,
      },
      (event) => {
        gotAnyEvent = true;
        if (!event || typeof event !== 'object') return;
        if (event.type === 'session') {
          if (event.session_id && !activeSessionId) {
            setActiveSessionId(event.session_id);
            loadSessions();
          }
          return;
        }
        if (event.type === 'tool_start') {
          setTurns((prev) => prev.map((t) => (
            t.id === streamingId ? { ...t, active_tool: event.name } : t
          )));
          return;
        }
        if (event.type === 'text_delta') {
          setTurns((prev) => prev.map((t) => (
            t.id === streamingId
              ? { ...t, content: (t.content || '') + (event.text || ''), active_tool: null }
              : t
          )));
          setTimeout(scrollToBottom, 0);
          return;
        }
        if (event.type === 'done') {
          finalized = true;
          setTurns((prev) => prev.map((t) => (
            t.id === streamingId
              ? {
                  ...t,
                  content: event.content || t.content || '',
                  tool_calls: event.tool_calls || [],
                  pending_confirmation: Boolean(event.pending_confirmation),
                  suggestions: Array.isArray(event.suggestions) ? event.suggestions : [],
                  streaming: false,
                  active_tool: null,
                }
              : t
          )));
          setSending(false);
          inputRef.current?.focus();
          setTimeout(scrollToBottom, 50);
          return;
        }
        if (event.type === 'error') {
          finalized = true;
          // Rate-limit responses (429) come back with a friendly multi-sentence
          // `message` plus `retry_after_seconds`. Show them for longer so the
          // user can actually read why they were blocked.
          const isRateLimit = event.status === 429 || (event.reason && [
            'user_burst', 'user_hourly', 'client_hourly',
            'client_daily_cost', 'session_turns', 'session_tokens',
          ].includes(event.reason));
          message.error(
            event.message || "Guru couldn't load that — try again?",
            isRateLimit ? { autoHideDuration: 15000 } : undefined,
          );
          setTurns((prev) => prev.filter((t) => t.id !== streamingId));
          setSending(false);
          inputRef.current?.focus();
        }
      },
    ).then(() => {
      // Safety net: if the stream ends without a "done" event for any reason,
      // wrap up so the UI doesn't stay stuck on "thinking".
      if (!finalized) {
        if (!gotAnyEvent) {
          message.error("Guru couldn't load that — try again?");
          setTurns((prev) => prev.filter((t) => t.id !== streamingId));
        } else {
          setTurns((prev) => prev.map((t) => (
            t.id === streamingId ? { ...t, streaming: false, active_tool: null } : t
          )));
        }
        setSending(false);
        inputRef.current?.focus();
      }
    }).catch((err) => {
      console.error('Chat stream failed', err);
      message.error("Guru couldn't load that — try again?");
      setTurns((prev) => prev.filter((t) => t.id !== streamingId));
      setSending(false);
      inputRef.current?.focus();
    });
  }, [
    activeSessionId, effectiveClientId, inputValue, loadSessions,
    message, scrollToBottom, sending, pendingAttachments,
  ]);

  // Deep-link follow-up: once clients are loaded, pre-select the one
  // passed via ?client=<id|slug>.
  useEffect(() => {
    const pending = pendingDeepLinkRef.current;
    if (!pending || !pending.clientParam || clients.length === 0) return;
    const match = clients.find(
      (c) => String(c.id) === String(pending.clientParam)
        || c.slug === pending.clientParam,
    );
    if (match && match.id !== selectedClientId) {
      setSelectedClientId(match.id);
    }
  }, [clients, selectedClientId]);

  // Deep-link follow-up: once sessions are loaded for the picked client,
  // pre-select the session passed via ?session=<id>. One-shot via
  // `sessionApplied` so the user isn't snapped back if they manually
  // pick a different session later.
  useEffect(() => {
    const pending = pendingDeepLinkRef.current;
    if (!pending || !pending.sessionParam || pending.sessionApplied) return;
    if (sessions.length === 0) return;
    const matches = sessions.some((s) => String(s.id) === String(pending.sessionParam));
    if (matches) {
      pending.sessionApplied = true;
      setActiveSessionId(pending.sessionParam);
    }
  }, [sessions]);

  // Deep-link auto-send: when ?autosend=1 came in, fire handleSend as
  // soon as we have a valid effective client. `sent` flag on the ref
  // prevents the effect re-firing on every handleSend re-creation.
  useEffect(() => {
    const pending = pendingDeepLinkRef.current;
    if (!pending || !pending.prompt || !pending.autosend || pending.sent) return;
    if (!effectiveClientId || sending) return;
    pending.sent = true;
    handleSend(pending.prompt);
  }, [effectiveClientId, sending, handleSend]);

  // --- Attachment handlers ---
  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || '';
        // strip the "data:type;base64," prefix
        const commaIdx = result.indexOf(',');
        resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
      };
      reader.onerror = () => reject(reader.error || new Error('read failed'));
      reader.readAsDataURL(file);
    });

  const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result || '');
      reader.onerror = () => reject(reader.error || new Error('read failed'));
      reader.readAsText(file);
    });

  const handleFilesPicked = useCallback(async (event) => {
    const files = Array.from(event.target?.files || []);
    if (!files.length) return;
    // Reset the input so picking the same file twice still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
    const available = ATTACHMENT_LIMITS.maxCount - pendingAttachments.length;
    if (available <= 0) {
      message.warning(`You can attach at most ${ATTACHMENT_LIMITS.maxCount} files per message.`);
      return;
    }
    const accepted = [];
    for (const file of files.slice(0, available)) {
      const ctype = (file.type || '').toLowerCase();
      try {
        if (ATTACHMENT_LIMITS.imageTypes.includes(ctype)) {
          if (file.size > ATTACHMENT_LIMITS.maxImageBytes) {
            message.warning(`${file.name} is too large (5MB max for images).`);
            continue;
          }
          const data = await readFileAsBase64(file);
          accepted.push({
            name: file.name,
            content_type: ctype,
            data_base64: data,
            size: file.size,
          });
        } else if (
          ATTACHMENT_LIMITS.textTypes.includes(ctype)
          || file.name.toLowerCase().endsWith('.csv')
          || file.name.toLowerCase().endsWith('.txt')
        ) {
          if (file.size > ATTACHMENT_LIMITS.maxTextBytes) {
            message.warning(`${file.name} is too large (50KB max for CSV/text).`);
            continue;
          }
          const text = await readFileAsText(file);
          accepted.push({
            name: file.name,
            content_type: ctype || 'text/csv',
            data_text: text,
            size: file.size,
          });
        } else {
          message.warning(`${file.name}: unsupported type (only images and CSV/text allowed).`);
        }
      } catch (err) {
        message.error(`Couldn't read ${file.name}.`);
      }
    }
    if (accepted.length) {
      setPendingAttachments((prev) => [...prev, ...accepted]);
    }
  }, [message, pendingAttachments.length]);

  const removeAttachment = useCallback((idx) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  // --- Voice input handlers ---
  const stopListening = useCallback(() => {
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.stop(); } catch (_) { /* ignore */ }
    }
    setIsListening(false);
  }, []);

  // Track the input value the user had BEFORE voice started, plus the
  // running pool of finalized chunks from the current voice session. On
  // every onresult fire we recompute the input as base + finals + interim,
  // which makes interim words appear live (word-by-word) without the
  // repeating-phrase bug that comes from appending interim into state.
  const voiceBaseRef = useRef('');
  const voiceFinalsRef = useRef('');

  const startListening = useCallback(() => {
    if (!voiceSupportedRef.current) {
      message.error('Voice input is not supported in this browser.');
      return;
    }
    const SpeechRecognitionCtor =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    // Snapshot the existing input so anything the user typed before pressing
    // mic is preserved while voice runs.
    voiceBaseRef.current = inputValue || '';
    voiceFinalsRef.current = '';

    const compose = (interim) => {
      const base = voiceBaseRef.current;
      const finals = voiceFinalsRef.current;
      const parts = [];
      if (base) parts.push(base);
      if (finals) parts.push(finals);
      if (interim) parts.push(interim);
      return parts
        .map((p, i) => (i === 0 ? p : p.trimStart()))
        .join(parts.length > 1 && !/\s$/.test(parts[0]) ? ' ' : '')
        .trimStart();
    };

    recognition.onresult = (event) => {
      let interimPart = '';
      // Walk only new results (resultIndex forward). Finalized ones get
      // committed to voiceFinalsRef permanently; non-final ones live as the
      // ephemeral interim that's recomputed every fire.
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          const trimmed = transcript.trim();
          if (trimmed) {
            const sep = voiceFinalsRef.current && !/\s$/.test(voiceFinalsRef.current)
              ? ' '
              : '';
            voiceFinalsRef.current = `${voiceFinalsRef.current}${sep}${trimmed}`;
          }
        } else {
          interimPart += transcript;
        }
      }
      setInputValue(compose(interimPart));
    };
    recognition.onerror = (event) => {
      console.warn('Voice recognition error:', event.error);
      setIsListening(false);
    };
    recognition.onend = () => {
      // Drop any leftover interim by recomposing with no interim — what's
      // committed in voiceFinalsRef stays in the input.
      setInputValue(compose(''));
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    setIsListening(true);
    try {
      recognition.start();
    } catch (err) {
      console.warn('Could not start voice recognition:', err);
      setIsListening(false);
    }
  }, [inputValue, message]);

  const toggleVoice = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // Stop listening if the user navigates away from the active session.
  useEffect(() => () => {
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.stop(); } catch (_) { /* ignore */ }
    }
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Always show input when a client scope is selected (single or All Clients)
  const canSend = !!selectedClientId;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        p: { xs: 1, md: 3 },
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2, flexShrink: 0 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <SmartToyIcon color="primary" />
          <Typography variant={isMobile ? 'h6' : 'h5'}>Guru</Typography>
          <Tooltip title="In beta — let us know if anything looks off.">
            <Chip
              label="Beta"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 22, fontWeight: 600 }}
            />
          </Tooltip>
        </Stack>
        {clients.length > 1 ? (
          <FormControl size="small" sx={{ minWidth: isMobile ? 160 : 260 }}>
            <InputLabel>Client</InputLabel>
            <Select
              label="Client"
              value={selectedClientId}
              onChange={(event) => {
                setSelectedClientId(event.target.value);
                setActiveSessionId(null);
                setTurns([]);
                setNewChatClientId('');
              }}
            >
              <MenuItem value={ALL_CLIENTS}>
                <em>All Clients</em>
              </MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </Stack>

      {/* Main layout: sidebar + chat */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 2 }}>
        {/* Session sidebar */}
        {selectedClientId && !isMobile ? (
          <Card
            sx={{
              width: 280,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewChat}
                size="small"
              >
                New Chat
              </Button>
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {sessionsLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={20} />
                </Box>
              ) : sessions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  No conversations yet
                </Typography>
              ) : (
                <List dense disablePadding>
                  {sessions.map((session) => (
                    <ListItemButton
                      key={session.id}
                      selected={session.id === activeSessionId}
                      onClick={() => handleSelectSession(session.id)}
                      sx={{
                        py: 1,
                        px: 1.5,
                        borderLeft: session.id === activeSessionId ? '3px solid' : '3px solid transparent',
                        borderColor: session.id === activeSessionId ? 'primary.main' : 'transparent',
                      }}
                    >
                      <ChatIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary', flexShrink: 0 }} />
                      <ListItemText
                        primary={session.title || 'Untitled chat'}
                        secondary={(() => {
                          const dateStr = new Date(session.updated_at).toLocaleDateString();
                          const author = session.created_by_name || '';
                          const clientStr = session.client_name || 'Unknown client';
                          if (isAllClients) {
                            return author
                              ? `${clientStr} · ${author}`
                              : clientStr;
                          }
                          return author ? `${author} · ${dateStr}` : dateStr;
                        })()}
                        primaryTypographyProps={{
                          variant: 'body2',
                          noWrap: true,
                          fontWeight: session.id === activeSessionId ? 600 : 400,
                        }}
                        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Card>
        ) : null}

        {/* Chat area */}
        <Card
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {/* Messages — centered max-width column for Claude-like readability */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: { xs: 1.5, md: 3 },
              py: 2,
              bgcolor: '#FAF9F6',
              '& > *': {
                maxWidth: 720,
                mx: 'auto',
              },
            }}
          >
            {!selectedClientId ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  Hi — I'm Guru. Pick a client above to start.
                  <br />
                  Or choose &ldquo;All Clients&rdquo; for portfolio-wide questions.
                </Typography>
              </Box>
            ) : loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress size={32} />
              </Box>
            ) : turns.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Stack spacing={1.5} alignItems="center">
                  <SmartToyIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                  <Typography variant="body1" color="text.secondary" textAlign="center">
                    {activeSession
                      ? `Continuing your conversation about ${effectiveClientName}.`
                      : effectiveClientName
                        ? `Hi — I'm Guru. Ask me anything about ${effectiveClientName}'s account.`
                        : isAllClients
                          ? "Hi — I'm Guru. Pick a client below and ask anything."
                          : 'Select a session or start a new chat.'}
                  </Typography>
                  {(effectiveClientName || isAllClients) ? (
                    <Stack spacing={1.5} sx={{ maxWidth: 560, width: '100%' }}>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.75, textAlign: 'center', fontWeight: 600, letterSpacing: 0.4 }}
                        >
                          ASK A QUESTION
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
                          {QUICK_QUESTIONS.map((item) => (
                            <Chip
                              key={item.label}
                              label={item.label}
                              variant="outlined"
                              size="small"
                              disabled={sending || !effectiveClientId}
                              onClick={() => handleSend(item.prompt)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Stack>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.75, textAlign: 'center', fontWeight: 600, letterSpacing: 0.4 }}
                        >
                          SUBMIT A CHANGE REQUEST
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
                          {QUICK_REQUESTS.map((item) => (
                            <Chip
                              key={item.label}
                              label={item.label}
                              color="primary"
                              variant="outlined"
                              size="small"
                              disabled={sending || !effectiveClientId}
                              onClick={() => handleSend(item.prompt)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  ) : null}
                </Stack>
              </Box>
            ) : (
              <Stack spacing={2}>
                {/* Show client chip in All Clients mode */}
                {isAllClients && effectiveClientName ? (
                  <Chip
                    label={effectiveClientName}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ alignSelf: 'center' }}
                  />
                ) : null}
                {turns.map((turn) => (
                  <ChatBubble key={turn.id} turn={turn} isMobile={isMobile} />
                ))}
                {(() => {
                  const lastTurn = turns.length > 0 ? turns[turns.length - 1] : null;
                  if (
                    !lastTurn
                    || lastTurn.role !== 'assistant'
                    || sending
                    || lastTurn.streaming
                  ) {
                    return null;
                  }
                  // Confirmation chips take precedence — they're a discrete
                  // gate. If both flags happen to be set, only show confirm.
                  if (lastTurn.pending_confirmation) {
                    return (
                      <Stack direction="row" spacing={1} sx={{ pl: 6, mt: -1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<CheckIcon />}
                          onClick={() => handleSend('Yes, submit it.')}
                          disabled={sending}
                        >
                          Yes, submit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => handleSend("Let me adjust before submitting.")}
                          disabled={sending}
                        >
                          Adjust
                        </Button>
                      </Stack>
                    );
                  }
                  // Followup-suggestion chips — only on the latest assistant
                  // turn, only when the LLM produced any, and only until the
                  // user sends something else.
                  const suggestions = Array.isArray(lastTurn.suggestions) ? lastTurn.suggestions : [];
                  if (!suggestions.length) return null;
                  return (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pl: 6, mt: -1 }}>
                      {suggestions.map((label, i) => (
                        <Chip
                          key={`${label}-${i}`}
                          label={label}
                          size="small"
                          variant="outlined"
                          onClick={() => handleSend(label)}
                          sx={{ cursor: 'pointer' }}
                          disabled={sending}
                        />
                      ))}
                    </Stack>
                  );
                })()}
                {sending ? (
                  <Box display="flex" alignItems="center" gap={1} pl={6}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Guru is looking that up…
                    </Typography>
                  </Box>
                ) : null}
                <div ref={messagesEndRef} />
              </Stack>
            )}
          </Box>

          {/* Input — centered max-width column to match the messages above */}
          {canSend ? (
            <Box
              sx={{
                px: { xs: 1.5, md: 3 },
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                flexShrink: 0,
                '& > *': {
                  maxWidth: 720,
                  mx: 'auto',
                },
              }}
            >
              {/* Mobile: new chat button */}
              {isMobile && selectedClientId ? (
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleNewChat}>
                    New Chat
                  </Button>
                  {activeSession ? (
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      {activeSession.title || 'Untitled'}
                    </Typography>
                  ) : null}
                </Stack>
              ) : null}
              {/* Inline client picker for All Clients mode when starting a new chat */}
              {isAllClients && !activeSessionId ? (
                <FormControl size="small" sx={{ mb: 1, minWidth: 200 }}>
                  <InputLabel>Chatting about</InputLabel>
                  <Select
                    label="Chatting about"
                    value={newChatClientId}
                    onChange={(e) => {
                      setNewChatClientId(e.target.value);
                      setTimeout(() => inputRef.current?.focus(), 50);
                    }}
                  >
                    {clients.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  multiline
                  maxRows={4}
                  size="small"
                  placeholder={
                    isAllClients && !effectiveClientId
                      ? 'Select a client above, then ask Guru anything…'
                      : `Ask Guru about ${effectiveClientName || 'this client'}…`
                  }
                  value={inputValue}
                  onChange={(event) => {
                    const next = event.target.value;
                    setInputValue(next);
                    // Any keystroke / paste / delete from the user re-anchors
                    // the voice session: whatever's in the box now becomes the
                    // new base, and any previously-collected finals are
                    // discarded. Without this, the next onresult fire would
                    // re-insert the words the user just deleted.
                    if (isListening) {
                      voiceBaseRef.current = next;
                      voiceFinalsRef.current = '';
                    }
                  }}
                  onKeyDown={handleKeyDown}
                  disabled={sending || (isAllClients && !effectiveClientId)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Attach an image or CSV (max 3 files)">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={
                                sending
                                || (isAllClients && !effectiveClientId)
                                || pendingAttachments.length >= 3
                              }
                            >
                              <AttachFileIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {voiceSupportedRef.current ? (
                          <Tooltip
                            title={
                              isListening
                                ? 'Stop dictating'
                                : 'Dictate your message with voice'
                            }
                          >
                            <span>
                              <IconButton
                                color={isListening ? 'error' : 'default'}
                                onClick={toggleVoice}
                                disabled={sending || (isAllClients && !effectiveClientId)}
                                size="small"
                              >
                                {isListening ? (
                                  <MicOffIcon fontSize="small" />
                                ) : (
                                  <MicIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                        <IconButton
                          color="primary"
                          onClick={() => handleSend()}
                          disabled={
                            (!inputValue.trim() && !pendingAttachments.length)
                            || sending
                            || (isAllClients && !effectiveClientId)
                          }
                          size="small"
                        >
                          <ArrowUpwardIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp,.csv,.txt,text/csv,text/plain"
                  multiple
                  onChange={handleFilesPicked}
                  style={{ display: 'none' }}
                />
                {pendingAttachments.length ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {pendingAttachments.map((att, i) => {
                      const sizeKb = att.size ? `${Math.round(att.size / 1024)}KB` : '';
                      return (
                        <Chip
                          key={`${att.name}-${i}`}
                          label={`${att.name}${sizeKb ? ` (${sizeKb})` : ''}`}
                          size="small"
                          onDelete={() => removeAttachment(i)}
                          deleteIcon={<CloseIcon />}
                        />
                      );
                    })}
                  </Stack>
                ) : null}
              </Stack>
            </Box>
          ) : null}
        </Card>
      </Box>
    </Box>
  );
}

function ChatBubble({ turn, isMobile }) {
  const isUser = turn.role === 'user';
  const isAssistant = turn.role === 'assistant';
  const toolCalls = turn.tool_calls || [];

  return (
    <Stack
      direction="row"
      spacing={1.5}
      justifyContent={isUser ? 'flex-end' : 'flex-start'}
      alignItems="flex-start"
    >
      {!isUser ? (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            flexShrink: 0,
          }}
        >
          <SmartToyIcon sx={{ fontSize: 18 }} />
        </Avatar>
      ) : null}

      <Box
        sx={{
          maxWidth: isMobile ? '85%' : '70%',
          bgcolor: isUser ? 'primary.main' : 'background.paper',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          borderRadius: 2.5,
          px: 2,
          py: 1.5,
          boxShadow: isUser ? 0 : 1,
          border: isUser ? 'none' : '1px solid',
          borderColor: 'divider',
        }}
      >
        {isUser ? (
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
          >
            {turn.content}
          </Typography>
        ) : (
          <Box
            sx={{
              '& p': { m: 0, mb: 1, lineHeight: 1.6, fontSize: '0.875rem' },
              '& p:last-child': { mb: 0 },
              '& strong': { fontWeight: 600 },
              '& h1, & h2, & h3, & h4': {
                mt: 1.5,
                mb: 0.5,
                fontWeight: 600,
                lineHeight: 1.3,
              },
              '& h3': { fontSize: '0.95rem' },
              '& h4': { fontSize: '0.875rem' },
              '& ul, & ol': { m: 0, mb: 1, pl: 2.5, fontSize: '0.875rem' },
              '& li': { mb: 0.25, lineHeight: 1.5 },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                mb: 1.5,
                mt: 0.5,
                fontSize: '0.8rem',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              },
              '& thead': {
                bgcolor: 'grey.100',
              },
              '& th': {
                textAlign: 'left',
                fontWeight: 600,
                borderBottom: '2px solid',
                borderColor: 'divider',
                px: 1.5,
                py: 0.75,
                whiteSpace: 'nowrap',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                color: 'text.secondary',
              },
              '& td': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                px: 1.5,
                py: 0.75,
              },
              '& tr:nth-of-type(even)': {
                bgcolor: 'grey.50',
              },
              '& tr:last-child td': { borderBottom: 'none' },
              '& hr': { my: 1.5, border: 'none', borderTop: '1px solid', borderColor: 'divider' },
              '& code': {
                bgcolor: 'action.hover',
                px: 0.5,
                borderRadius: 0.5,
                fontSize: '0.8rem',
                fontFamily: 'monospace',
              },
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer" />
                ),
              }}
            >
              {turn.content || ''}
            </ReactMarkdown>
            {turn.active_tool ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <CircularProgress size={10} />
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Looking up {turn.active_tool.replace(/^fetch_/, '').replace(/_/g, ' ')}…
                </Typography>
              </Stack>
            ) : null}
            {turn.streaming && !turn.active_tool && !turn.content ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={10} />
                <Typography variant="caption" color="text.secondary">Guru is looking that up…</Typography>
              </Stack>
            ) : null}
          </Box>
        )}
        {/* Tool-call name pills used to render here for debugging — removed
            because they leaked internal tool names (e.g. "propose budget
            change") to clients and added visual noise without useful detail.
            If we want a debug view later, build a proper collapsible panel
            with full request/response, gated on user.is_staff. */}
      </Box>

      {isUser ? (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'grey.400',
            flexShrink: 0,
          }}
        >
          <PersonIcon sx={{ fontSize: 18 }} />
        </Avatar>
      ) : null}
    </Stack>
  );
}

export default AgentChatPage;
